/** @jsxImportSource @emotion/react */
import { SiriuswareMappingProduct} from './productMappings';
import {Dispatch, SetStateAction} from 'react';
import * as React from 'react';
import TooltipView from '../layout/tooltip/TooltipView';
import {css} from '@emotion/react';
import {doNothing} from '../language/module';
import {MediumText} from '../styles/styles';
import {BLACK, DARK_TOOLTIP, MUTED_GREY, WHITE} from '../styles/colors';
import {ERROR_BORDER} from '../forms/styles';
import {FADE_IN_ANIMATION} from '../styles/animations';
import {IAppState} from '../core/redux/IAppState';
import * as R from 'ramda';
import {PropValidationMsg} from '../forms/validation/propValidationMsg';
import {Input} from '../forms/elements/Input';
import {useDispatch, useSelector} from 'react-redux';
import {updateRowAction} from './updateRow';
import errorImgRed from '../assets/icons/failureTriangleRed.svg';
import checkCircleFilled from '../assets/icons/checkCircleFilled.svg'
import { getResource } from '../language/resources/getResource';
import { HYDRATION_ERRORS_CELL_HEIGHT_PX, HYDRATION_ERRORS_CELL_WIDTH_PX, HYDRATION_ERRORS_TOOLTIP_WIDTH, MAPPING_ERROR_TOOLTIP_DELAY_MS, MAPPING_STATUS_ICON_SIZE_PX, MAPPING_ERROR_TOOLTIP_WIDTH, MAX_SW_CATEGORY_LENGTH, MAX_SW_DEPARTMENT_LENGTH, MAX_SW_ITEM_LENGTH } from './constants';
import ErrorCountSummary from '../layout/ErrorCountSummary';
import { TooltipFC } from '../layout/TooltipFC';
import { HydrationErrorRetryButton } from './HydrationErrorReplayButton';
import { cellPlaceholder, LoadStatus } from './productMappingLoadStatus';

type Props = {
  entitlement: SiriuswareMappingProduct;
  locked: boolean;
  changedIds: string[];
  setChangedIds: Dispatch<SetStateAction<string[]>>;
  showHydrationErrors: boolean;
  mappingStatus: LoadStatus;
  hydrationStatus: LoadStatus;
};

const styles = {
  rowItemHeight:         css`
    height: 40px;
  `,
  error:                 css`
    ${MediumText};
    background-color: ${BLACK};
    color: ${WHITE};
    padding: 10px;
    white-space: pre-line;
  `,
  errorBorder:           css`
    ${ERROR_BORDER};
    box-sizing: border-box;
  `,
  row:                   css`
    td {
      vertical-align: middle !important;
      ${MediumText};
      ${FADE_IN_ANIMATION};
    }

    td > * {
      ${FADE_IN_ANIMATION};
    }
  `,
  checkboxWtp: css`
    margin-left: 40px;
  `,
  editButton:            css`
    &:hover {
      cursor: pointer;
    }
  `,
  centeredBox:           css`
    display: block;
    margin: auto;
  `,
  centeredTooltipFC:      css`
  display: block; 
  margin: auto; 
  position: relative; 
  width: fit-content;
  `,
  capitalInput: css`
    textTransform: 'uppercase'
  `,
  placeholder: css`
    display: block;
    min-width: ${MAPPING_STATUS_ICON_SIZE_PX}px;
    height: ${MAPPING_STATUS_ICON_SIZE_PX}px;
    line-height: ${MAPPING_STATUS_ICON_SIZE_PX}px;
    text-align: center;
    color: ${MUTED_GREY};
  `,
  // Same size whatever it shows, so the table doesn't shift
  hydrationCell: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: ${HYDRATION_ERRORS_CELL_WIDTH_PX}px;
    min-height: ${HYDRATION_ERRORS_CELL_HEIGHT_PX}px;
  `,
};

/**
 *  Renders row for Siriusware Products
 * @param props = {
 *   entitlement: SiriuswareMappingProduct;
 *   locked: boolean;
 *   changedIds: string[];
 *   setChangedIds: Dispatch<SetStateAction<string[]>>;
 * }
 */
export const SwProductRow = (props: Props) => {

  const errors = [];
  const entitlement: SiriuswareMappingProduct = props.entitlement;

  const dispatch = useDispatch();

  const resources = useSelector((state: IAppState) => state.locale)

  const mappingPlaceholder = cellPlaceholder(props.mappingStatus);
  const hydrationPlaceholder = cellPlaceholder(props.hydrationStatus);
  // Retry needs a clean mapping, which only a Mapping Status that has finished reloading can vouch for
  const canRetryHydration = props.mappingStatus === 'loaded' && entitlement.isError === false;
  // Why the count shows in place of Retry
  const noRetryTooltipKey = props.mappingStatus === 'loaded'
    ? 'PRODUCT_MAPPING_HYDRATION_ERRORS_TOOLTIP'
    : 'PRODUCT_MAPPING_HYDRATION_ERRORS_TOOLTIP_STATUS_LOADING';

  const handleUpdate = (field, event) => {

    switch (field) {
      case 'department':
        dispatch(updateRowAction({
          product: entitlement.product,
          season: entitlement.season,
          ageBin: entitlement.ageBin,
          fulfillmentSeason: entitlement.fulfillmentSeason,
          department: event.target.value.toUpperCase(),
          category: entitlement.category,
          item: entitlement.item,
          productType: entitlement.productType,
          wtpReload: entitlement.wtpReload,
          id: entitlement.id,
          isResortConfigurable: entitlement.isResortConfigurable,
          isError: entitlement.isError,
          errorDescription: entitlement.errorDescription,
          entitlementTypeId: entitlement.entitlementTypeId,
          fulfillmentSeasonId: entitlement.fulfillmentSeasonId,
          hydrationErrors: entitlement.hydrationErrors,
        }));
        break;
      case 'category':
        dispatch(updateRowAction({
          product: entitlement.product,
          season: entitlement.season,
          ageBin: entitlement.ageBin,
          fulfillmentSeason: entitlement.fulfillmentSeason,
          department: entitlement.department,
          category: event.target.value.toUpperCase(),
          item: entitlement.item,
          productType: entitlement.productType,
          wtpReload: entitlement.wtpReload,
          id: entitlement.id,
          isResortConfigurable: entitlement.isResortConfigurable,
          isError: entitlement.isError,
          errorDescription: entitlement.errorDescription,
          entitlementTypeId: entitlement.entitlementTypeId,
          fulfillmentSeasonId: entitlement.fulfillmentSeasonId,
          hydrationErrors: entitlement.hydrationErrors,
        }));
        break;
      case 'item':
        dispatch(updateRowAction({
          product: entitlement.product,
          season: entitlement.season,
          ageBin: entitlement.ageBin,
          fulfillmentSeason: entitlement.fulfillmentSeason,
          department: entitlement.department,
          category: entitlement.category.toUpperCase(),
          item: event.target.value,
          productType: entitlement.productType,
          wtpReload: entitlement.wtpReload,
          id: entitlement.id,
          isResortConfigurable: entitlement.isResortConfigurable,
          isError: entitlement.isError,
          errorDescription: entitlement.errorDescription,
          entitlementTypeId: entitlement.entitlementTypeId,
          fulfillmentSeasonId: entitlement.fulfillmentSeasonId,
          hydrationErrors: entitlement.hydrationErrors,
        }));
        break;
      case 'productType':
        dispatch(updateRowAction({
          product: entitlement.product,
          season: entitlement.season,
          ageBin: entitlement.ageBin,
          fulfillmentSeason: entitlement.fulfillmentSeason,
          department: entitlement.department,
          category: entitlement.category,
          item: entitlement.item,
          productType: event.target.value,
          wtpReload: entitlement.wtpReload,
          id: entitlement.id,
          isResortConfigurable: entitlement.isResortConfigurable,
          isError: entitlement.isError,
          errorDescription: entitlement.errorDescription,
          entitlementTypeId: entitlement.entitlementTypeId,
          fulfillmentSeasonId: entitlement.fulfillmentSeasonId,
          hydrationErrors: entitlement.hydrationErrors,
        }));
        break;
      case 'wtpReload':
        dispatch(updateRowAction({
          product: entitlement.product,
          season: entitlement.season,
          ageBin: entitlement.ageBin,
          fulfillmentSeason: entitlement.fulfillmentSeason,
          department: entitlement.department,
          category: entitlement.category,
          item: entitlement.item,
          productType: entitlement.productType,
          wtpReload: !entitlement.wtpReload,
          id: entitlement.id,
          isResortConfigurable: entitlement.isResortConfigurable,
          isError: entitlement.isError,
          errorDescription: entitlement.errorDescription,
          entitlementTypeId: entitlement.entitlementTypeId,
          fulfillmentSeasonId: entitlement.fulfillmentSeasonId,
          hydrationErrors: entitlement.hydrationErrors,
        }));
        break;
      default: break;
    }

    if (!props.changedIds.includes(entitlement.id)) {
      props.setChangedIds( prevArr => [...prevArr, entitlement.id]);
    }

  }

  const getError = (prop: string): string | undefined => R.path<string>(
    ['msg'],
    R.find<PropValidationMsg>(error => error.propName === prop, errors),
  );

  const makeEditableCell = (propName: string, children: React.ReactNode) => (
    <td>
      <TooltipView
        side='bottom'
        showTooltip={!!getError(propName)}
        floatingContentCss={css`width: 100%;`}
        tooltipContent={<span css={styles.error}>{getError(propName)}</span>}
        activateOnHover={false}
        onClickInside={doNothing}
        onClickOutside={doNothing}
        onHoverInside={doNothing}
        onHoverOutside={doNothing}
      >
        {children}
      </TooltipView>
    </td>
  );

  return (
    <tr>
      {// Product not editable
        <td css={css`min-width: 225px;`}>{entitlement.product}</td>
      }
      {
        // Season not editable
        <td css={css`min-width: 125px;text-align: center`}>{entitlement.season}</td>
      }
      {
        // Age  not editable
        <td css={css`min-width: 125px;text-align: center`}>{entitlement.ageBin}</td>
      }
      {
        // Fulfillment Season not editable
        <td>{props.entitlement.fulfillmentSeason}</td>
      }

      {
        // Department editable
        !props.locked ?
          makeEditableCell(
            'Department',
            <Input
              type='text'
              maxLength={MAX_SW_DEPARTMENT_LENGTH}
              title={getResource(resources, 'RESORT_MAPPING_COLUMN_DEPARTMENT')}
              property='department'
              placeholder=''
              value={props.entitlement.department?.trim()}
              onChange={e => handleUpdate('department', e)}
              css={styles.capitalInput}
            />
          ) : <td>{props.entitlement.department?.trim()}</td>
      }

      {
        // Category  editable
        !props.locked ?
          makeEditableCell(
            'category',
            <Input
              type='text'
              maxLength={MAX_SW_CATEGORY_LENGTH}
              title={getResource(resources, 'RESORT_MAPPING_COLUMN_CATEGORY')}
              property='category'
              placeholder=''
              value={props.entitlement.category?.trim()}
              onChange={e => handleUpdate('category', e)}
              css={styles.capitalInput}
            />
          ) : <td>{props.entitlement.category?.trim()}</td>
      }

      {
        // Item editable
        !props.locked ?
          makeEditableCell(
            'item',
            <Input
              type='text'
              maxLength={MAX_SW_ITEM_LENGTH}
              title={getResource(resources, 'RESORT_MAPPING_COLUMN_ITEM')}
              property='Item'
              placeholder=''
              value={props.entitlement.item?.trim() || ''}
              onChange={e => handleUpdate('item', e)}
              css={styles.capitalInput}
            />
          ) : <td>{props.entitlement.item?.trim()}</td>
      }

      {
        // Product Type editable
        /*
        !props.locked ?
          makeEditableCell(
            'ProductType',
            <Input
              type='text'
              title={getResource(resources, 'RESORT_MAPPING_COLUMN_PRODUCT_TYPE')}
              property='productType'
              placeholder=''
              value={props.entitlement.productType || ''}
              onChange={e => handleUpdate('productType', e)}
              css={css``}
            />
          ) : <td>{props.entitlement.productType}</td>
           */
      }

      {
        // WtpReload editable
        /*
          makeEditableCell(
            'wtpReload',
            renderCheckbox(props.locked)
          )
            */
      }

      {
        // Mapping Status not editable
        mappingPlaceholder ?
        (<td title={getResource(resources, mappingPlaceholder.titleKey)}>
          <span css={styles.placeholder}>{mappingPlaceholder.text}</span>
        </td>) :
        entitlement.isError === null ?
        (<td></td>) :
        <td>
        { entitlement.isError ?
            (<div css={styles.centeredTooltipFC}>
              <TooltipFC
                backgroundColor={`${DARK_TOOLTIP}`}
                delay={MAPPING_ERROR_TOOLTIP_DELAY_MS}
                width={MAPPING_ERROR_TOOLTIP_WIDTH}
                content={<span>{entitlement.errorDescription}</span>} 
                children={<img css={styles.centeredBox} src={errorImgRed} alt="error"/>}
              />
            </div>) 
            : (<img css={styles.centeredBox} src={checkCircleFilled} alt=""/>)
        }
        </td>
      }

      { 
        /* hydration error count */
        props.showHydrationErrors &&
        <td css={css`max-width: 150px;`} title={hydrationPlaceholder && getResource(resources, hydrationPlaceholder.titleKey)}>
          <div css={styles.hydrationCell}>
          { hydrationPlaceholder ?
              <ErrorCountSummary placeholder={hydrationPlaceholder.text}/>
              : !entitlement.hydrationErrors?.errorCount ?
              <ErrorCountSummary value={0}/> 
              : !canRetryHydration
            ? <div css={styles.centeredTooltipFC}>  
                <TooltipFC
                  backgroundColor={`${DARK_TOOLTIP}`}
                  width={HYDRATION_ERRORS_TOOLTIP_WIDTH}
                  content={<span>{getResource(resources, noRetryTooltipKey)}</span>}
                  children={
                    <ErrorCountSummary value={entitlement.hydrationErrors?.errorCount ?? 0}/>
                  }/>
              </div> :
                <HydrationErrorRetryButton 
                  productName={entitlement.product}
                  fulfillmentSeasonDescription={entitlement.fulfillmentSeason}
                  hydrationErrors={entitlement.hydrationErrors}/>
          }
          </div>
        </td>
      }
    </tr>
  )
};
